<template>
  <div class="!pt-0">
    <div class="flex flex-col space-y-2.5 max-h-screen pt-16 pb-2.5">
      <!-- Main Content Card -->
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-y-auto mt-2.5 relative">
        <template v-if="fetchSubcontractorClaimAction.is(States.LOADING)">
          <div class="p-4 flex flex-col justify-center items-center space-y-2 font-medium">
            <span>
              Claim Loading...
            </span>

            <AppSpinner />
          </div>
        </template>

        <template v-else-if="claim && fetchSubcontractorClaimAction.is(States.COMPLETE)">
          <!-- Header with Status and Actions -->
          <div class="p-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
            <div class="flex items-center justify-between">
              <div class="flex items-center space-x-4">
                <div class="flex-shrink-0">
                  <div class="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-5 h-5 text-blue-600">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>
                  </div>
                </div>

                <div class="flex-1">
                  <div class="flex items-center space-x-3">
                    <h1 class="text-lg font-bold text-gray-900">
                      Subcontractor {{ claimType }} Claims
                    </h1>

                    <template v-if="claim">
                      <span class="text-gray-400">•</span>

                      <h2 class="text-lg font-semibold text-gray-900">
                        {{ subcontractorPackage?.name }}
                      </h2>

                      <span class="text-gray-400">•</span>

                      <h2 class="text-lg font-semibold text-gray-900">
                        Claim {{ claim.claim_number }}
                      </h2>

                      <template v-if="claim?.status === ClaimStatus.certified">
                        <span class="text-gray-400">•</span>

                        <p class="text-lg text-gray-900">
                          <span class="font-medium">Amount Payable (excl. GST) {{ australianCurrency(claim.totalPayable) }}</span>
                        </p>
                      </template>

                      <span
                        class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                        :class="claimStatusBadgeClass"
                      >
                        <span
                          class="w-1.5 h-1.5 rounded-full mr-1.5"
                          :class="claimStatusBadgeDotClass"
                        />
                        {{ ClaimStatusTitles[enumKeyFromValue(ClaimStatus, claim.status)] }}
                      </span>
                    </template>
                  </div>

                  <div class="flex items-center space-x-3">
                    <p
                      v-if="claim"
                      class="text-sm text-gray-600"
                    >
                      <span class="font-medium">Period</span> {{ new Date(claim.start).toDateString() }} - {{ new Date(claim.end).toDateString() }}
                    </p>

                    <span
                      v-if="claim?.draftIssuedAt"
                      class="text-gray-400"
                    >•</span>

                    <p
                      v-if="claim?.draftIssuedAt"
                      class="text-sm text-gray-600"
                    >
                      <span class="font-medium">Issued</span> {{ claim.draftIssuedAt.toLocaleString() }}
                    </p>

                    <span
                      v-if="claim?.claimIssuedAt"
                      class="text-gray-400"
                    >•</span>

                    <p
                      v-if="claim?.claimIssuedAt"
                      class="text-sm text-gray-600"
                    >
                      <span class="font-medium">Submitted</span> {{ claim.claimIssuedAt.toLocaleString() }}
                    </p>

                    <span
                      v-if="claim?.submittedAt"
                      class="text-gray-400"
                    >•</span>

                    <p
                      v-if="claim?.submittedAt"
                      class="text-sm text-gray-600"
                    >
                      <span class="font-medium">Certified</span> {{ claim.submittedAt.toLocaleString() }}
                    </p>

                    <div
                      v-if="showRetention && !isReadOnly && typingRetention"
                      class="flex items-center"
                    >
                      <span class="text-sm font-medium text-gray-900 flex-shrink-0">Retention (excl. GST)</span>

                      <div class="w-full">
                        <AppCurrencyInput
                          ref="retentionInput"
                          v-model="retention"
                          class="form-input ml-2 w-3/4"
                          type="text"
                          name="retention"
                          @keyup.esc="typingRetention = false"
                          @blur="updateRetention"
                          @keyup.enter="$event.target.blur()"
                        />
                      </div>
                    </div>

                    <button
                      v-else-if="showRetention && !isReadOnly && claim"
                      type="button"
                      class="flex items-center space-x-2 cursor-pointer btn-icon btn-icon-default"
                      @click="showRetentionInput"
                    >
                      <p class="text-sm font-medium text-gray-900">
                        Retention (excl. GST) {{ australianCurrency(claim.retention) }}
                      </p>

                      <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="h-4 w-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
    />
</svg>
                    </button>

                    <div v-else-if="showRetention">
                      <p class="text-sm font-medium text-gray-900">
                        Retention (excl. GST) {{ australianCurrency(claim?.retention) }}
                      </p>
                    </div>
                  </div>

                  <div>
                    <p
                      v-if="projectSupplier"
                      class="text-sm text-gray-600"
                    >
                      <span class="font-medium">Vendor</span> {{ projectSupplier.supplier?.name }}
                    </p>
                  </div>

                  <div
                    v-if="enableSopaStatement && (claim?.status === ClaimStatus.certified || claim?.status === ClaimStatus.subcontractor_claim) && claim?.sopa_disclaimer"
                    class="mt-3"
                  >
                    <label class="flex items-start space-x-2 cursor-default">
                      <input
                        :checked="claim.sopa_disclaimer"
                        type="checkbox"
                        name="sopa_disclaimer"
                        data-test="sopa-disclaimer"
                        class="form-checkbox mt-0.5"
                        disabled
                      >

                      <span class="text-sm text-gray-900">
                        {{ sopaStatementText }}
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              <!-- Action Buttons -->
              <div
                v-if="!userStore.isClientManager()"
                class="flex items-center space-x-3"
              >
                <button
                  v-if="(claim?.status === ClaimStatus.draft || claim?.status === ClaimStatus.draft_subcontractor) && projectsStore.project?.unarchived"
                  type="button"
                  class="btn btn--primary-blue flex items-center space-x-2"
                  @click="issueClaim"
                >
                  <svg

    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
 class="w-4 h-4">
    <path
    stroke-linecap="round"
    stroke-linejoin="round"
    stroke-width="2"
    d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
    />
</svg>

                  <span v-if="claim?.status === ClaimStatus.draft">Issue Claim</span>

                  <span v-else>Resend Claim</span>
                </button>

                <button
                  v-if="claim && claim.status === ClaimStatus.subcontractor_claim && projectsStore.project?.unarchived"
                  type="button"
                  class="btn btn--secondary-blue flex items-center space-x-2"
                  @click="requestApproval(claim.id)"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
    <path stroke-linecap="round" stroke-linejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
</svg>

                  <span>{{ $t('Subcontract.SubcontractorClaimShow.button.requestApproval') }}</span>
                </button>

                <div class="flex items-center space-x-2">
                  <button
                    type="button"
                    class="btn btn--secondary-blue flex items-center space-x-2"
                    @click.prevent="showImageModal = true"
                  >
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" class="w-4 h-4">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"/>
</svg>

                    <span>Attach Files</span>
                  </button>

                  <button
                    v-if="claim?.status === ClaimStatus.draft && projectsStore.project?.unarchived"
                    type="button"
                    class="btn btn--secondary-alert flex items-center space-x-2"
                    @click.prevent="deleteClaim"
                  >
                    <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
    />
</svg>

                    <span>Delete Claim</span>
                  </button>

                  <button
                    v-if="claim?.status === ClaimStatus.subcontractor_claim && projectsStore.project?.unarchived"
                    type="button"
                    class="btn btn--primary-blue flex items-center space-x-2"
                    @click="certifyClaim"
                  >
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" class="w-4 h-4">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
</svg>

                    <span>Certify Claim</span>
                  </button>
                </div>

                <!-- Action Buttons for Certified Status -->
                <div
                  v-if="claim?.status === ClaimStatus.certified"
                  class="flex items-center space-x-2"
                >
                  <button
                    type="button"
                    class="btn btn--secondary-blue flex items-center space-x-2"
                    :class="{ 'opacity-50': claimExportAction.is(States.LOADING) }"
                    :disabled="claimExportAction.is(States.LOADING)"
                    @click.prevent="exportClaim"
                  >
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" class="w-4 h-4">
<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>

                    <span>Export to PDF</span>
                  </button>

                  <AppActionMenu>
                    <template #trigger>
                      <button
                        type="button"
                        class="btn btn--secondary-blue p-2"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
  <path stroke-linecap="round" stroke-linejoin="round" d="M12 6.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 12.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 18.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5Z" />
</svg>
                      </button>
                    </template>

                    <template #default="{ close }">
                      <button
                        type="button"
                        class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                        :class="{ 'opacity-50': issueCertificateAction.is(States.LOADING) }"
                        :disabled="issueCertificateAction.is(States.LOADING)"
                        @click="close(); issueCertificate()"
                      >
                        <svg

    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
 class="w-4 h-4">
    <path
    stroke-linecap="round"
    stroke-linejoin="round"
    stroke-width="2"
    d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
    />
</svg>

                        <span>Issue Certificate</span>
                      </button>

                      <button
                        v-if="hasDayworks"
                        type="button"
                        class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                        :class="{ 'opacity-50': dayworksExportAction.is(States.LOADING) }"
                        :disabled="dayworksExportAction.is(States.LOADING)"
                        @click="close(); exportDayworks()"
                      >
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" class="w-4 h-4">
<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>

                        <span>Export Dayworks</span>
                      </button>

                      <button
                        v-if="isLatestClaim && userStore.isRole(['company_admin', 'project_admin']) && projectsStore.project?.unarchived"
                        type="button"
                        class="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2"
                        :class="{ 'opacity-50': uncertifyClaimAction.is(States.LOADING) }"
                        :disabled="uncertifyClaimAction.is(States.LOADING)"
                        @click="close(); uncertifyClaim()"
                      >
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" class="w-4 h-4">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"/>
</svg>

                        <span>Uncertify Claim</span>
                      </button>
                    </template>
                  </AppActionMenu>
                </div>
              </div>
            </div>
          </div>

          <!-- Table Content -->
          <div
            v-if="claim"
            class="overflow-x-auto"
          >
            <LumpSumClaimTable
              v-if="isLumpSum"
              :claim-id="claimId"
              :project-id="projectId"
              :package-id="packageId"
            />

            <RatesClaimTable
              v-else
              :claim-id="claimId"
              :project-id="projectId"
              :package-id="packageId"
            />
          </div>
        </template>
      </div>
    </div>

    <ClaimAttachmentsModal
      v-if="showImageModal"
      :claim-id="claimId"
      @close="showImageModal = false;"
    />
  </div>
</template>

<script setup lang="ts">
import ClaimAttachmentsModal from '@/components/HeadContract/ProgressClaimsLite/ClaimAttachmentsModal.vue';
import LumpSumClaimTable from '@/components/Subcontract/SubcontractorClaimShow/LumpSumClaimTable.vue';
import RatesClaimTable from '@/components/Subcontract/SubcontractorClaimShow/RatesClaimTable.vue';
import { promptCertificateLayout } from '@/composables/useCertificateLayoutSwal';
import { useRequestClaimApproval } from '@/composables/useRequestClaimApproval';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import {
  enumKeyFromValue,
  falsey,
  getNextTimeBarDueIn,
  getNextTimeBarDueInBadgeClass,
  getNextTimeBarDueInBadgeDotClass,
  timeZone,
} from '@/helpers';
import { australianCurrency } from '@/helpers';
import { ClaimStatus, ClaimStatusTitles } from '@/models/Claim';
import Claim from '@/models/Claim';
import { useClaimsStore } from '@/store/claims';
import { useDayworksCostsStore } from '@/store/dayworksCosts';
import { useLaboursStore } from '@/store/labours';
import { usePackagesStore } from '@/store/packages';
import { usePlantsStore } from '@/store/plants';
import { useProjectsStore } from '@/store/projects';
import { useTradesStore } from '@/store/trades';
import { useUserStore } from '@/store/user';
import { useVariationOrderCategoriesStore } from '@/store/variationOrderCategories';
import { useVariationOrdersStore } from '@/store/variationOrders';
import { useWbsCodesStore } from '@/store/wbsCodes';
import { DateTime } from 'luxon';
import Swal from 'sweetalert2';
import { computed, nextTick, ref, useTemplateRef, watch } from 'vue';
import { useRouter } from 'vue-router';

const props = defineProps<{
  projectId: string;
  claimId: string;
  packageId: string;
}>();

const claimsStore = useClaimsStore();
const dayworksCostsStore = useDayworksCostsStore();
const laboursStore = useLaboursStore();
const plantsStore = usePlantsStore();
const router = useRouter();
const tradesStore = useTradesStore();
const userStore = useUserStore();
const packagesStore = usePackagesStore();
const variationOrdersStore = useVariationOrdersStore();
const wbsCodesStore = useWbsCodesStore();
const projectsStore = useProjectsStore();
const { requestApproval } = useRequestClaimApproval();
const fetchTradesAction = useStoreApiAction(tradesStore.fetchTrades);
const fetchVariationOrdersAction = useStoreApiAction(variationOrdersStore.fetchPackageApprovedVariations);
const fetchSubcontractorClaimAction = useStoreApiAction(claimsStore.showSubcontractorClaim);
const fetchClaimLabourAction = useStoreApiAction(laboursStore.fetchForSubcontractorClaim);
const fetchClaimPlantAction = useStoreApiAction(plantsStore.fetchForSubcontractorClaim);
const fetchPackageAction = useStoreApiAction(packagesStore.show);
const indexWbsCodesAction = useStoreApiAction(wbsCodesStore.index);

const enableSopaStatement = computed(() => {
  return projectsStore.project?.subcontract_settings?.enable_sopa_statement ?? true;
});

const sopaStatementText = computed(() => {
  return projectsStore.project?.subcontract_settings?.sopa_statement_text ?? '';
});

fetchTradesAction.request(props.projectId, { boq_type: 'direct' }).catch((error) => {
  console.log(error);
});

fetchVariationOrdersAction.request(props.projectId, props.packageId).catch((error) => {
  console.log(error);
});

indexWbsCodesAction.request(props.projectId).catch((error) => {
  console.log(error);
});

watch(() => props.projectId, (newValue, oldValue) => {
  if(newValue !== oldValue) {
    router.push({ name: 'SubcontractorClaimsDashboard' });
  }
});

watch(() => props.claimId, () => {
  fetchSubcontractorClaimAction.request(props.projectId, props.claimId);
  fetchClaimLabourAction.request(props.projectId, props.claimId);
  fetchClaimPlantAction.request(props.projectId, props.claimId);
}, { immediate: true });

watch(() => props.packageId, () => {
  fetchPackageAction.request(props.packageId);
}, { immediate: true });

const claim = computed(() => {
  if(props.claimId) {
    const claimQuery = claimsStore.models
      .with('trades', (query) => {
        query.with('trade_items');
      })
      .with('rates_costs', (query) => {
        query.with('package_trade_item');
      })
      .with('lump_sum_costs', (query) => {
        query.with('package_trade_item');
      })
      .with('variation_order_costs')
      .with('dayworks_costs')
      .where(
        'project_id',
        parseInt(props.projectId),
      );

    return claimQuery.find(parseInt(props.claimId));
  }

  return undefined;
});

const subcontractorPackage = computed(() => {
  return packagesStore.models.find(parseInt(props.packageId));
});

const isLumpSum = computed(() => {
  return claim.value?.boq_type === 'subcontractor_lump_sum';
});

const isLatestClaim = computed(() => {
  if(!claim.value) return false;

  const latestCertifiedClaim = claimsStore.models
    .where('project_id', parseInt(props.projectId))
    .where('boq_type', claim.value.boq_type)
    .where('status', ClaimStatus.certified)
    .whereHas('package_claim', (query) => {
      query.where('package_id', parseInt(props.packageId));
    })
    .orderBy('claim_number', 'desc')
    .first();

  return falsey(latestCertifiedClaim) || latestCertifiedClaim.id === claim.value.id;
});

const packageForClaim = computed(() => {
  return packagesStore.models
    .with('subcontractor', (query) => {
      query.with('projects', (query) => {
        query.where('project_id', parseInt(props.projectId));
        query.with('supplier');
      });
    })
    .find(parseInt(props.packageId));
});

const projectSupplier = computed(() => {
  return packageForClaim.value?.subcontractor?.projects[0];
});

const certifyClaimAction = useStoreApiAction(claimsStore.certifySubcontractorClaim);
const uncertifyClaimAction = useStoreApiAction(claimsStore.uncertifySubcontractorClaim);

const issueClaim = async () => {
  if(!claim.value) return;

  try {
    // Get supplier contact email
    const supplierEmail = projectSupplier.value.contact_email;

    // Show SweetAlert prompt for optional email override
    const result = await Swal.fire({
      title: 'Issue Claim',
      html: `
        <div class="text-left">
          <p class="mb-4">This claim will be sent to the subcontractor via email.</p>
          <p class="mb-4 text-sm text-gray-600">
            <strong>Default email:</strong> ${supplierEmail || 'No contact email set'}
          </p>
          <p class="mb-4 text-sm text-gray-600">
            You can override the email address below if needed.
          </p>
        </div>
        <input
          id="email-input"
          type="email"
          class="swal2-input"
          placeholder="Override email (optional)"
          value="${supplierEmail || ''}"
        />
      `,

      showCancelButton: true,
      confirmButtonText: 'Issue Claim',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#10b981',
      cancelButtonColor: '#6b7280',
      preConfirm: () => {
        const emailInput = document.getElementById('email-input') as HTMLInputElement;
        const email = emailInput?.value?.trim();

        // If no email provided and no default email, show error
        if(!email && !supplierEmail) {
          Swal.showValidationMessage('Please provide an email address');

          return false;
        }

        // If email provided, validate it
        if(email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          Swal.showValidationMessage('Please enter a valid email address');

          return false;
        }

        return email || supplierEmail;
      },

      allowOutsideClick: false,
      allowEscapeKey: true,
    });

    if(result.isConfirmed && result.value) {
      const emailToUse = result.value as string;

      await claimsStore.issueSubcontractorClaim(props.projectId, props.claimId, {
        email: emailToUse,
        draft_issued_at_time_zone: timeZone,
      });

      // Show success message
      await Swal.fire({
        title: 'Claim Issued',
        text: 'The claim has been successfully sent to the subcontractor.',
        icon: 'success',
        confirmButtonColor: '#10b981',
      });

      // Reload data to reflect the updated claim status
      // await loadData();
    }
  } catch (error) {
    console.error('Failed to issue claim:', error);

    await Swal.fire({
      title: 'Error',
      text: 'Failed to issue the claim. Please try again.',
      icon: 'error',
      confirmButtonColor: '#ef4444',
    });
  }
};

const certifyClaim = () => {
  Swal.fire({
    icon: 'warning',
    title: 'Certify Claim?',
    text: 'Are you sure you want to certify this claim? You will not be able to adjust any amounts after certifying.',
    showCancelButton: true,
    showConfirmButton: true,
  }).then((result) => {
    if(result.isConfirmed) {
      certifyClaimAction.request(props.projectId, props.claimId, { submitted_at_time_zone: timeZone }).then(() => {
        Swal.fire({
          title: 'Claim Certified',
          icon: 'success',
        });
      }).catch((error) => {
        if(error.status === 422) {
          Swal.fire({
            title: 'Failed to certify claim',
            text: error.data.message,
            icon: 'error',
          });
        }
      });
    }
  });
};

const uncertifyClaim = () => {
  Swal.fire({
    icon: 'warning',
    title: 'Uncertify Claim?',
    text: 'Are you sure you want to uncertify this claim? This will allow the costs to be altered.',

    showCancelButton: true,
    showConfirmButton: true,
  }).then((result) => {
    if(result.isConfirmed) {
      uncertifyClaimAction.request(props.projectId, props.claimId).then(() => {
        Swal.fire({
          title: 'Claim Uncertified',
          icon: 'success',
        });
      }).catch((error) => {
        Swal.fire({
          title: 'Failed to uncertify claim',
          icon: 'error',
        });
      });
    }
  });
};

const retention = ref(0);
const typingRetention = ref(false);
const updateClaimAction = useStoreApiAction(claimsStore.updateClaim);

watch(claim, (newValue) => {
  if(newValue) {
    retention.value = newValue.retention;
  }
}, { immediate: true });

const retentionInput = useTemplateRef('retentionInput');

const showRetentionInput = () => {
  typingRetention.value = true;

  nextTick(() => {
    retentionInput.value?.focusInput();
  });
};

const updateRetention = () => {
  if(!typingRetention.value) {
    retention.value = claim.value.retention;

    return;
  }

  if(falsey(retention.value)) {
    typingRetention.value = false;
    retention.value = claim.value.retention;

    return;
  }

  updateClaimAction.request(claim.value.id, { retention: retention.value }).then(() => {
    typingRetention.value = false;
  }).catch((error) => {
    Swal.fire({
      icon: 'error',
      title: 'Failed to update retention',
      text: error.data?.message,
    });

    typingRetention.value = true;
  });
};

const deleteClaim = () => {
  Swal.fire({
    icon: 'warning',
    title: 'Delete Claim?',
    text: 'Are you sure you want to delete this claim? This cannot be undone.',
    showCancelButton: true,
    showConfirmButton: true,
  }).then((result) => {
    if(result.isConfirmed) {
      claimsStore.deleteClaim(claim.value.id).then(() => {
        router.push({ name: 'SubcontractorClaimsDashboard' });

        Swal.fire({
          icon: 'success',
          title: 'Claim deleted.',
        });
      });
    }
  });
};

const claimExportAction = useStoreApiAction(claimsStore.exportSubcontractorClaim);
const dayworksExportAction = useStoreApiAction(dayworksCostsStore.exportDayworks);
const issueCertificateAction = useStoreApiAction(claimsStore.issueSubcontractorCertificate);

const issueCertificate = async () => {
  if(!claim.value) return;

  const compact = await promptCertificateLayout();

  if(compact === undefined) {
    return;
  }

  const supplierEmail = projectSupplier.value?.contact_email;

  const result = await Swal.fire({
    title: 'Issue Certificate',
    html: `
      <div class="text-left">
        <p class="mb-4">This certificate will be sent to the subcontractor via email.</p>
        <p class="mb-4 text-sm text-gray-600">
          <strong>Default email:</strong> ${supplierEmail || 'No contact email set'}
        </p>
        <p class="mb-4 text-sm text-gray-600">
          You can override the email address below if needed.
        </p>
      </div>
      <input
        id="email-input"
        type="email"
        class="swal2-input"
        placeholder="Override email (optional)"
        value="${supplierEmail || ''}"
      />
    `,

    showCancelButton: true,
    confirmButtonText: 'Issue Certificate',
    cancelButtonText: 'Cancel',
    confirmButtonColor: '#10b981',
    cancelButtonColor: '#6b7280',
    preConfirm: () => {
      const emailInput = document.getElementById('email-input') as HTMLInputElement;
      const email = emailInput?.value?.trim();

      if(!email && !supplierEmail) {
        Swal.showValidationMessage('Please provide an email address');

        return false;
      }

      if(email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        Swal.showValidationMessage('Please enter a valid email address');

        return false;
      }

      return email || supplierEmail;
    },

    allowOutsideClick: false,
    allowEscapeKey: true,
  });

  if(result.isConfirmed && result.value) {
    const emailToUse = result.value as string;

    issueCertificateAction.request(props.projectId, props.claimId, {
      email: emailToUse,
      draft_issued_at_time_zone: timeZone,
      compact,
    }).then(() => {
      Swal.fire({
        title: 'Certificate Issued',
        text: 'The certificate is being generated and will be sent to the subcontractor when it is ready.',
        icon: 'success',
        confirmButtonColor: '#10b981',
      });
    }).catch((error) => {
      if(error.status === 429) {
        Swal.fire({
          icon: 'error',
          title: 'Certificate is currently being generated',
          text: error.data.message,
        });
      }
    });
  }
};

const exportClaim = async () => {
  const compact = await promptCertificateLayout();

  if(compact === undefined) {
    return;
  }

  let applySignature = undefined;

  if(userStore.userSignature && userStore.isRole(['company_admin'], false)) {
    const result = await Swal.fire({
      title: 'Sign Claim Export?',
      input: 'checkbox',
      inputPlaceholder: `
        Add my signature to the claim export
      `,
      showCancelButton: true,
      confirmButtonText: 'Export',
    });

    if(result.value === 1) {
      applySignature = { apply_signature: true };
    }

    if(result.isDenied || result.isDismissed) {
      return;
    }
  }

  claimExportAction.request(props.projectId, props.claimId, { ...applySignature, compact }).then(() => {
    Swal.fire({
      icon: 'success',
      title: 'Claim Exported.',
      text: 'Claim is being exported to PDF and will be sent to your email when it is ready.',
    });
  }).catch((error) => {
    if(error.status === 429) {
      Swal.fire({
        icon: 'error',
        title: 'PDF is currently being exported',
        text: error.data.message,
      });
    } else {
      Swal.fire({
        icon: 'error',
        title: 'Failed to Export.',
      });
    }
  });
};

const hasMenuActions = computed(() => {
  if(!claim.value || userStore.isClientManager()) {
    return false;
  }

  const unarchived = projectsStore.project?.unarchived;
  const status = claim.value.status;

  return (
    (status === ClaimStatus.draft && unarchived) ||
    (status !== ClaimStatus.certified && unarchived) ||
    status === ClaimStatus.certified
  );
});

const hasDayworks = computed(() => {
  const claimId = parseInt(props.claimId);

  return laboursStore.models.where('$claim_id', claimId).get().length > 0 ||
    plantsStore.models.where('$claim_id', claimId).get().length > 0 ||
    dayworksCostsStore.models.where('claim_id', claimId).get().length > 0;
});

const exportDayworks = () => {
  dayworksExportAction.request(props.projectId, props.claimId).then(() => {
    Swal.fire({
      icon: 'success',
      title: 'Dayworks Exported.',
      text: 'Dayworks export is being generated and will be sent to your email when it is ready.',
    });
  }).catch((error) => {
    if(error.status === 429) {
      Swal.fire({
        icon: 'error',
        title: 'Dayworks export is currently being generated',
        text: error.data.message,
      });
    } else {
      Swal.fire({
        icon: 'error',
        title: 'Failed to Export Dayworks.',
      });
    }
  });
};

const showImageModal = ref(false);

const claimSelectedMonth = computed(() => {
  if(!claim.value?.start) {
    return undefined;
  }

  return DateTime.fromISO(claim.value.start).startOf('month');
});

const nextTimeBarDueIn = computed(() => {
  if(!claim.value || !projectsStore.project || !claimSelectedMonth.value) {
    return undefined;
  }

  return getNextTimeBarDueIn(claim.value, projectsStore.project, claimSelectedMonth.value);
});

const claimStatusBadgeClass = computed(() => getNextTimeBarDueInBadgeClass(nextTimeBarDueIn.value, claim.value));
const claimStatusBadgeDotClass = computed(() => getNextTimeBarDueInBadgeDotClass(nextTimeBarDueIn.value, claim.value));

const isReadOnly = computed(() => {
  return userStore.isClientManager() || !projectsStore.project?.unarchived;
});

const claimType = computed(() => {
  return isLumpSum.value ? 'Lump Sum' : 'Rates';
});

const showRetention = computed(() => {
  return subcontractorPackage.value?.performance_security === 'retention';
});
</script>

<style scoped></style>
